import { useState } from "react";

import { dollars } from "../lib/format";
import type { DecisionRow as DR } from "../lib/types";

type Props = { row: DR };

/**
 * Render the quantity. Prefer `qty_decimal` when present (preserves
 * fractional precision for crypto + partial-share trades); fall back
 * to `qty` for legacy rows from before the 2026-05-18 schema upgrade.
 *
 * Formatting:
 *  - qty >= 1: show up to 4 decimals, trim trailing zeros (so "229" not "229.0000")
 *  - qty <  1: show 6 decimals (BTC 0.04036 / ETH 0.1234 etc.)
 *  - qty == 0: "0" (denied entries that never sized)
 */
function formatQty(row: DR): string {
  const q = row.qty_decimal != null ? row.qty_decimal : row.qty;
  if (q === 0) return "0";
  if (q >= 1) {
    return q
      .toFixed(4)
      .replace(/\.?0+$/, "");
  }
  return q.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
}

export function DecisionRow({ row }: Props) {
  const [open, setOpen] = useState(false);
  let reasons: string[] = [];
  try {
    reasons = JSON.parse(row.reasons_json);
  } catch {
    reasons = [];
  }
  const allowed = row.allowed === 1;
  return (
    <li className="border-b border-ink-700 px-3 py-2 text-sm">
      <button
        className="flex w-full items-center justify-between text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-baseline gap-3">
          <span
            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
              allowed
                ? "bg-accent-ok/20 text-accent-ok"
                : "bg-accent-err/20 text-accent-err"
            }`}
          >
            {allowed ? "ALLOWED" : "DENIED"}
          </span>
          <span className="font-mono font-medium text-slate-100">
            {row.side} {formatQty(row)} {row.symbol}
          </span>
          {row.limit_price_cents != null && (
            <span className="text-xs text-slate-400">
              @ {dollars(row.limit_price_cents)}
            </span>
          )}
          {/* Live-safety tag: real money trades get a prominent badge.
              Paper trades carry a subdued one. Rows from before the
              bot started sending is_live show no badge (we don't
              assume). */}
          {row.is_live === 1 && (
            <span
              className="rounded bg-amber-900/60 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-200"
              title="Real-money trade"
            >
              💸 LIVE
            </span>
          )}
          {row.is_live === 0 && (
            <span
              className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"
              title="Paper trade"
            >
              📄 paper
            </span>
          )}
          {row.setup_name && (
            // Detail-view: which named detector/screen fired. Mostly
            // intraday, but any strategy can populate it.
            <span
              className="rounded bg-sky-900/40 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-sky-300"
              title="Setup name"
            >
              {row.setup_name}
            </span>
          )}
        </div>
        <span className="font-mono text-xs text-slate-500">
          {row.timestamp_utc.slice(11, 19)}Z
        </span>
      </button>
      {open && reasons.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-6 text-xs text-slate-400">
          {reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      {open && reasons.length === 0 && allowed && (
        <div className="mt-2 pl-6 text-xs italic text-slate-500">
          no reasons recorded
        </div>
      )}
      {open && (row.open_positions_count != null || row.thesis_id) && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 pl-6 text-[11px] text-slate-500">
          {row.open_positions_count != null && (
            <span>
              <span className="text-slate-400">positions at check:</span>{" "}
              {row.open_positions_count}
            </span>
          )}
          {row.thesis_id && (
            <span className="font-mono">
              <span className="text-slate-400">thesis:</span> {row.thesis_id}
            </span>
          )}
        </div>
      )}
    </li>
  );
}
